import { createPublicSite, } from "../core/index.js";
import { createPublicHeadDescriptor, createPublicIndexablePolicy, createPublicNoIndexPolicy, extendPublicHeadDescriptor, } from "../seo/index.js";
import { renderPublicMarkdown, toPublicMarkdownPath, } from "../adapters/markdown/index.js";
import { createPublicResourceCatalog, } from "../resources/index.js";
import { createPublicMultiSitemap } from "../sitemap/index.js";
import { buildPublicRobotsTxt } from "../robots/index.js";
import { auditPublicSite } from "../audit/index.js";
import { createPublicSiteHandler, } from "../adapters/http/site.js";
import { createPublicSitemapHandler } from "../adapters/http/sitemap.js";
import { createExpressPublicSiteMiddleware } from "../adapters/express/index.js";
import { createPublicStaticSiteArtifacts, } from "../ssg/index.js";
const DefaultCopy = {
    title: "Page not found",
    description: "The requested page could not be found.",
};
const DefaultUnavailableCopy = {
    title: "Temporarily unavailable",
    description: "This page is temporarily unavailable.",
};
const DefaultPrivateCopy = {
    title: "Private page",
    description: "Authentication is required to access this page.",
};
function normalizeLocales(locales) {
    return Array.isArray(locales)
        ? locales
        : Object.entries(locales).map(([code, locale]) => ({
            code: code,
            locale: locale,
        }));
}
function normalizeCache(cache, fallback) {
    const value = cache ?? fallback;
    if (value === false)
        return { kind: "no-store" };
    if (typeof value === "number")
        return {
            kind: "public",
            maxAgeSeconds: value,
            staleIfErrorSeconds: value,
        };
    return {
        kind: "public",
        maxAgeSeconds: value.maxAge,
        staleIfErrorSeconds: value.staleIfError ?? value.maxAge,
    };
}
function normalizeLocalePolicy(value, fallback) {
    const localized = value ?? fallback;
    if (localized === false)
        return { policy: "none" };
    const mode = localized === true ? "redirect" : typeof localized === "string"
        ? localized
        : localized.mode;
    const redirect = typeof localized === "object"
        ? localized.redirect ?? "accept-language"
        : "accept-language";
    const xDefault = typeof localized === "object"
        ? localized.xDefault ?? (mode === "indexable" ? "unlocalized" : "default-locale")
        : mode === "indexable" ? "unlocalized" : "default-locale";
    return {
        policy: mode === "redirect"
            ? {
                kind: "localized",
                unlocalized: "redirect",
                redirect,
                xDefault: "default-locale",
            }
            : {
                kind: "localized",
                unlocalized: "indexable",
                xDefault: xDefault === "default-locale" ? "default-locale" : "unlocalized",
            },
        ...(typeof localized === "object" && localized.paths
            ? {
                paths: Object.fromEntries(Object.entries(localized.paths).filter((entry) => (typeof entry[1] === "string"))),
            }
            : {}),
    };
}
function normalizeRender(route, fallback) {
    if (route.private)
        return { kind: "spa" };
    if (typeof route.render === "object")
        return route.render;
    const kind = route.render ?? (route.loader ? "ssr" : fallback);
    return kind === "hybrid"
        ? { kind: "hybrid", prerender: "all", fallback: "ssr" }
        : { kind };
}
function normalizeMarkdown(value, fallback, content) {
    const markdown = value ?? (content ? { content } : fallback);
    if (markdown === false || markdown === "seo")
        return { kind: "seo" };
    if (markdown === true)
        return { kind: "seo" };
    if (typeof markdown === "string")
        return { kind: "renderer", key: markdown };
    if ("content" in markdown)
        return { kind: "content", key: markdown.content };
    return { kind: "renderer", key: markdown.renderer };
}
function normalizeLlms(value, fallback, markdown) {
    const llms = value ?? fallback;
    if (llms === false)
        return false;
    if (llms === true)
        return {
            visibility: "primary",
            full: markdown.kind === "seo" ? "index-only" : "include",
        };
    if (llms === "include" || llms === "index-only")
        return { visibility: "primary", full: llms };
    if (llms === "optional")
        return {
            visibility: "optional",
            full: markdown.kind === "seo" ? "index-only" : "include",
        };
    return llms;
}
function normalizeSitemap(value, fallback, lastModified, source, dynamic) {
    const sitemap = value ?? fallback;
    if (sitemap === false || (dynamic && !source))
        return false;
    const settings = sitemap === true ? {} : sitemap;
    const changefreq = settings.changeFrequency ?? "monthly";
    const priority = settings.priority ?? 0.5;
    if (source)
        return { kind: "collection", source, changefreq, priority };
    return {
        lastmod: settings.lastModified ?? lastModified,
        changefreq,
        priority,
    };
}
function isRedirect(route) {
    return "from" in route;
}
function compileRoutes(options) {
    const defaults = options.defaults ?? {};
    const defaultLocalized = defaults.localized ?? "redirect";
    const defaultCache = defaults.cache ?? { maxAge: 300, staleIfError: 300 };
    const defaultSitemap = defaults.sitemap ?? true;
    const defaultMarkdown = defaults.markdown ?? "seo";
    const defaultLlms = defaults.llms ?? true;
    const lastModified = options.lastModified ?? new Date().toISOString().slice(0, 10);
    const routes = [];
    for (const [key, input] of Object.entries(options.routes)) {
        if (isRedirect(input)) {
            routes.push({
                kind: "redirect",
                key,
                path: input.from,
                location: input.to,
                statusCode: input.status,
            });
            continue;
        }
        const dynamic = input.path.includes(":");
        const privateRoute = input.private === true;
        if (!privateRoute && !input.seo)
            throw new Error(`Public route '${key}' must declare seo title and description.`);
        const locale = normalizeLocalePolicy(privateRoute ? false : input.localized, defaultLocalized);
        const render = normalizeRender(input, defaults.render ?? "ssg");
        const markdown = privateRoute
            ? { kind: "seo" }
            : normalizeMarkdown(input.markdown, defaultMarkdown, input.content);
        const seo = input.seo ?? {
            ...(options.seo.private ?? DefaultPrivateCopy),
        };
        const routeSeo = {
            titleKey: seo.title,
            descriptionKey: seo.description,
            image: seo.image,
            titleSuffix: seo.titleSuffix,
            indexing: privateRoute
                ? { kind: "noindex", reason: "private", follow: false }
                : seo.indexing,
        };
        const common = {
            key,
            componentId: input.component,
            localePolicy: locale.policy,
            access: privateRoute ? "authenticated" : "public",
            crawl: privateRoute ? "disallow" : "allow",
            render,
            data: input.loader ? { loaderKey: input.loader } : undefined,
            cache: privateRoute ? { kind: "no-store" } : normalizeCache(input.cache, defaultCache),
            headResolverKey: input.head,
            seo: routeSeo,
            sitemap: privateRoute
                ? false
                : normalizeSitemap(input.sitemap, defaultSitemap, lastModified, input.source, dynamic),
            markdown,
            llms: privateRoute ? false : normalizeLlms(input.llms, defaultLlms, markdown),
        };
        if (dynamic) {
            routes.push({
                kind: "dynamic-page",
                pathPattern: input.path,
                localizedPathPatterns: locale.paths,
                resources: input.source ? { source: input.source } : undefined,
                ...common,
                sitemap: common.sitemap,
            });
        }
        else
            routes.push({
                kind: "page",
                path: input.path,
                localizedPaths: locale.paths,
                browserComponentId: input.browserComponent,
                name: input.name,
                contentKey: input.content,
                ...common,
            });
    }
    return { routes, lastModified };
}
function createSiteHeadResolver(site, seo) {
    const translate = seo.translate ?? ((_locale, value) => value);
    const resolveCopy = (definition, locale) => ({
        title: translate(locale, definition.title),
        description: translate(locale, definition.description),
    });
    const statusHead = (path, definition, reason) => {
        const normalizedPath = site.normalizePath(path);
        const locale = site.getLocaleFromPath(normalizedPath) ?? site.defaultLocale;
        const copy = resolveCopy(definition, locale);
        return createPublicHeadDescriptor({
            site: seo,
            path: normalizedPath,
            htmlLang: locale,
            title: copy.title,
            description: copy.description,
            canonicalPath: normalizedPath,
            indexing: createPublicNoIndexPolicy(reason),
        });
    };
    const resolveNotFoundHead = (path) => statusHead(path, seo.notFound ?? DefaultCopy, "not-found");
    const resolveUnavailableHead = (path) => statusHead(path, seo.unavailable ?? DefaultUnavailableCopy, "unavailable");
    const resolveBaseHead = (path) => {
        const normalizedPath = site.normalizePath(path);
        const resolution = site.resolvePath(normalizedPath);
        if (resolution.type === "not-found")
            return resolveNotFoundHead(normalizedPath);
        if (resolution.type !== "render")
            return statusHead(normalizedPath, resolution.type === "app-shell"
                ? seo.private ?? DefaultPrivateCopy
                : seo.unavailable ?? DefaultUnavailableCopy, resolution.type === "app-shell" ? "private" : "redirect");
        const copy = resolveCopy({
            title: resolution.route.seo.titleKey,
            description: resolution.route.seo.descriptionKey,
        }, resolution.locale);
        const title = [
            copy.title,
            ...(resolution.route.seo.titleSuffix ?? []).map((suffix) => suffix.kind === "literal"
                ? suffix.value
                : translate(resolution.locale, suffix.key)),
        ].join(" — ");
        const context = {
            path: resolution.routePath,
            canonicalPath: resolution.canonicalPath,
            locale: resolution.locale,
            route: resolution.route,
            data: {},
            copy: { title, description: copy.description },
        };
        return createPublicHeadDescriptor({
            site: seo,
            path: resolution.routePath,
            htmlLang: resolution.locale,
            title,
            description: copy.description,
            canonicalPath: resolution.canonicalPath,
            indexing: resolution.indexable
                ? resolution.route.seo.indexing ?? createPublicIndexablePolicy()
                : createPublicNoIndexPolicy("private"),
            image: resolution.route.seo.image,
            alternates: site.shouldApplyHreflang(resolution.routePath)
                ? site.buildAlternateHrefs(resolution.routePath, seo.url)
                : [],
            structuredData: seo.structuredData?.(context),
        });
    };
    return {
        translate,
        resolveBaseHead,
        resolveNotFoundHead,
        resolveUnavailableHead,
    };
}
function bindPublicSite(site, capabilities = {}) {
    const loaders = capabilities.loaders ?? {};
    const headResolvers = capabilities.headResolvers ?? {};
    const staticOnly = capabilities.resources === false;
    const resourceSources = capabilities.resources || {};
    const resources = createPublicResourceCatalog({
        site,
        sources: resourceSources,
        allowMissingSources: staticOnly,
    });
    const sitemap = createPublicMultiSitemap({
        site,
        siteUrl: site.seoIdentity.url,
        sources: resourceSources,
        allowMissingSources: staticOnly,
    });
    const resolveHead = (path, data = {}, canonicalPath) => {
        const targetPath = canonicalPath ?? path;
        const base = site.resolveHead(targetPath);
        const match = site.matchRoute(path);
        const resolverKey = match?.route.headResolverKey;
        if (!resolverKey)
            return base;
        const resolver = headResolvers[resolverKey];
        if (!resolver)
            throw new Error(`Missing public head resolver '${resolverKey}'.`);
        return extendPublicHeadDescriptor(base, resolver({
            pathname: site.normalizePath(path),
            canonicalPath: site.normalizePath(targetPath),
            route: match.route,
            params: match.params,
            data,
        }));
    };
    const renderMarkdown = (options) => {
        const path = site.normalizePath(options.routeUrl);
        const locale = site.getLocaleFromPath(path) ?? site.defaultLocale;
        return renderPublicMarkdown({
            site,
            routePath: path,
            routeUrl: options.routeUrl,
            siteUrl: site.seoIdentity.url,
            locale,
            translate: (key) => site.resolveCopy(locale, key),
            getContent: capabilities.getMarkdownContent,
            renderers: capabilities.markdownRenderers,
            loaders,
            renderNotFound: () => capabilities.renderMarkdownNotFound?.(locale)
                ?? `# ${site.resolveNotFoundHead(path).title}\n\n${site.resolveNotFoundHead(path).description}`,
            renderUnavailable: () => capabilities.renderMarkdownUnavailable?.(locale)
                ?? `# ${site.resolveUnavailableHead(path).title}\n\n${site.resolveUnavailableHead(path).description}`,
            redirectLocaleCode: options.redirectLocaleCode,
            redirectStatusCode: 307,
            formatRedirectPath: options.formatRedirectPath,
            signal: options.signal,
        });
    };
    const sitemapHandler = createPublicSitemapHandler({ sitemap });
    const createHandlerOptions = (options = {}) => ({
        ...options,
        site,
        robots: () => buildPublicRobotsTxt({ site }),
        sitemap: sitemapHandler,
        markdown: ({ request, routeUrl, formatRedirectPath }) => renderMarkdown({
            routeUrl,
            formatRedirectPath,
            signal: request.signal,
            redirectLocaleCode: options.resolveRedirectLocaleCode?.(request),
        }),
    });
    const createStaticSite = async (options) => {
        const sitemapDocuments = await sitemap.getDocuments();
        const renderedSitemaps = await Promise.all(sitemapDocuments.map(async (document) => ({
            outputPath: `sitemaps/${document.name}.xml`,
            contents: await sitemap.renderDocument(document.name) ?? "",
            kind: "sitemap",
        })));
        const documents = [
            ...(options.documents ?? []),
            ...(options.discoveryDocuments ?? []),
            {
                outputPath: "robots.txt",
                contents: buildPublicRobotsTxt({ site }),
                kind: "robots",
            },
        ];
        return createPublicStaticSiteArtifacts({
            ...options,
            site,
            resourceCatalog: resources,
            resolveHead: (routePath, resource) => resolveHead(routePath, resource?.data),
            resolveNotFoundHead: site.resolveNotFoundHead,
            renderMarkdown: async (routePath) => {
                const response = await renderMarkdown({ routeUrl: routePath });
                if (response.statusCode !== 200 || response.redirectTo)
                    throw new Error(`Cannot prerender Markdown '${routePath}' (status ${response.statusCode}).`);
                return response.markdown;
            },
            getMarkdownOutputPath: (routePath) => toPublicMarkdownPath(site, routePath).replace(/^\//, ""),
            sitemapDocuments: [
                {
                    outputPath: "sitemap.xml",
                    contents: await sitemap.renderIndex(),
                    kind: "sitemap",
                },
                ...renderedSitemaps,
            ],
            documents,
        });
    };
    return {
        site,
        loaders,
        headResolvers,
        resources,
        sitemap,
        robots: () => buildPublicRobotsTxt({ site }),
        resolveHead,
        renderMarkdown,
        audit: () => auditPublicSite({
            site,
            capabilities: {
                loaders,
                headResolvers,
                renderers: capabilities.markdownRenderers,
                hasContent: capabilities.getMarkdownContent
                    ? (key) => site.locales.every((locale) => (capabilities.getMarkdownContent?.(key, locale.locale) !== null))
                    : undefined,
            },
            resources: resources.iterate(),
        }),
        createHttpHandler: (options = {}) => createPublicSiteHandler(createHandlerOptions(options)),
        createExpressMiddleware: (options = {}) => createExpressPublicSiteMiddleware(createHandlerOptions(options)),
        createStaticSite,
    };
}
export function definePublicSite(options) {
    const locales = normalizeLocales(options.locales);
    const defaultLocale = locales.find((locale) => locale.code === options.defaultLocale)?.locale;
    if (!defaultLocale)
        throw new Error(`Default locale '${options.defaultLocale}' is not declared.`);
    const compiled = compileRoutes(options);
    const graph = createPublicSite({
        locales,
        defaultLocale,
        defaultLocaleCode: options.defaultLocale,
        sitemapLastModified: compiled.lastModified,
        origin: options.origin,
        robots: options.robots,
        routes: compiled.routes,
    });
    const seoIdentity = {
        ...options.seo,
        url: options.origin,
    };
    const head = createSiteHeadResolver(graph, seoIdentity);
    let site;
    site = Object.assign(graph, {
        seoIdentity,
        resolveCopy: head.translate,
        resolveHead: head.resolveBaseHead,
        resolveNotFoundHead: head.resolveNotFoundHead,
        resolveUnavailableHead: head.resolveUnavailableHead,
        bind: (capabilities) => bindPublicSite(site, capabilities),
    });
    return site;
}
export function definePublicSiteCapabilities(capabilities) {
    return capabilities;
}
//# sourceMappingURL=index.js.map